import React, { useState, useEffect } from 'react';
import { X, Clock, MessageSquare, CreditCard, PlayCircle, Loader2 } from 'lucide-react';
import axiosClient from '../../api/axiosClient';
import { motion, AnimatePresence } from 'framer-motion';

export default function ClientTimelineModal({ clientEmail, onClose }) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const res = await axiosClient.get(`/admin/clients/${clientEmail}/timeline`);
        setTimeline(res.data.timeline || []);
      } catch (err) {
        console.error('Failed to load timeline', err);
      } finally {
        setLoading(false);
      }
    };
    if (clientEmail) {
      fetchTimeline();
    }
  }, [clientEmail]);

  const getIcon = (type) => {
    switch(type) {
      case 'trial': return <PlayCircle size={16} className="text-white" />;
      case 'enquiry': return <MessageSquare size={16} className="text-white" />;
      case 'payment': return <CreditCard size={16} className="text-white" />;
      case 'bill': return <Clock size={16} className="text-white" />;
      default: return <Clock size={16} className="text-white" />;
    }
  };

  const getColor = (type) => {
    switch(type) {
      case 'trial': return 'bg-blue-500';
      case 'enquiry': return 'bg-amber-500';
      case 'payment': return 'bg-emerald-500';
      case 'bill': return 'bg-purple-500';
      default: return 'bg-slate-500';
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-[#1e2336] rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]"
        >
          <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800">
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Client Journey Timeline</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">{clientEmail}</p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="p-6 overflow-y-auto flex-1 bg-slate-50 dark:bg-[#0b1220]">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary-500 animate-spin mb-4" />
                <p className="text-slate-500 dark:text-slate-400">Loading timeline...</p>
              </div>
            ) : timeline.length === 0 ? (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                No activity recorded for this client yet.
              </div>
            ) : (
              <div className="relative border-l-2 border-slate-200 dark:border-slate-700 ml-4 space-y-8">
                {timeline.map((event, index) => (
                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    key={index} 
                    className="relative pl-8"
                  >
                    <div className={`absolute -left-[17px] top-0.5 w-8 h-8 rounded-full flex items-center justify-center border-4 border-slate-50 dark:border-[#0b1220] shadow-sm ${getColor(event.type)}`}>
                      {getIcon(event.type)}
                    </div>
                    
                    <div className="bg-white dark:bg-[#1e2336] p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-slate-900 dark:text-white capitalize">{event.type}</h4>
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-md">
                          {event.date ? `${new Date(event.date).toLocaleDateString()} ${new Date(event.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}` : '-'}
                        </span>
                      </div>
                      <p className="text-slate-700 dark:text-slate-300 text-sm whitespace-pre-wrap">{event.description ?? "-"}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
